(ns app.front.view.admin.tenant-management.features-tab
  "Features tab of the tenant detail view: a collapsible section for the domain
  group's features, then one per authority group, each a
  `app.front.ui.toggle-list`. Toggling uses `:admin/tenant-set-feature`, which
  routes to the tenant's provider."
  (:require [app.domain.group.model :as group-model]
            [app.front.entities :as entities]
            [app.front.ui.section :as section]
            [app.front.ui.toggle-list :as ui-toggle-list]
            [app.front.util :refer [for-frag]]
            [app.front.view.admin.domain-groups.municipality-groups :as mg]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

;; ---------------------------------------------------------------------------
;; Turning a feature into a toggle row
;; ---------------------------------------------------------------------------

;; ---------------------------------------------------------------------------
;; Toggling
;; ---------------------------------------------------------------------------

(rf/reg-event-fx ::set-feature
  (fn [_ [_ {:keys [tenant-id group-id feature features enabled? version]}]]
    ;; The entity-db is patched first so the switch does not lag behind the round
    ;; trip, and put back by `::set-feature.done` if the command fails.
    {:dispatch     [::entities/update-entity group-id
                    {:group/features ((if enabled? conj disj) (or features #{}) feature)}]
     :comm/command {:command          :admin/tenant-set-feature
                    :data             (p/assoc-if {:domain-group-id tenant-id
                                                   :group-id        group-id
                                                   :feature         feature
                                                   :enabled?        enabled?}
                                                  :entity/version version)
                    :on-success       [::entities/apply-changes]
                    :finally-dispatch [::set-feature.done group-id feature features enabled?]
                    :notify           {:success :changes-saved}}}))

(rf/reg-event-fx ::set-feature.done
  (fn [_ [_ group-id feature features enabled? status]]
    (when (not= status 200)
      {:dispatch [::entities/update-entity group-id
                  {:group/features ((if enabled? disj conj) features feature)}]})))

;; ---------------------------------------------------------------------------
;; Turning a feature into a toggle row
;; ---------------------------------------------------------------------------

(defn- feature-label
  "The short `group.feature.title/*` as the row's name where one exists, and
  `group.feature/*` as its description."
  [tr tr-quiet feature]
  (let [title (tr-quiet [(keyword "group.feature.title" (name feature))])
        name' (tr [feature])]
    (if title
      {:label title :description name'}
      {:label name'})))

(defn- requirement-description
  "Sub-features explain which main feature they depend on, matching the existing
  admin view."
  [tr feature]
  (when-let [main (group-model/sub-feature->main-feature feature)]
    (tr [:group.feature.issue/requires-main] [(tr [main])])))

(defn- available-features
  "Features that are relevant for `group`: either already on, or offered for this
  group and country."
  [group country features]
  (filter #(or (contains? (:group/features group) %)
               (group-model/feature-available? group country %))
          features))

(defn- feature-row
  [{:keys [tr tr-quiet tenant-id group feature global-flags mobile-app-safe?]}]
  (let [enabled?                    (contains? (:group/features group) feature)
        ;; The mobile app may only be enabled for groups whose configuration is
        ;; known to be safe for it.
        mobile-block?               (and (= feature :group.feature/mobile-app)
                                         (not (mobile-app-safe? (:db/id group))))
        disabled?                   (or mobile-block?
                                        (not (group-model/can-configure-feature? group
                                                                                 global-flags
                                                                                 feature
                                                                                 (not enabled?))))
        issue                       (when disabled?
                                      (group-model/configure-feature-issue group
                                                                           global-flags
                                                                           feature
                                                                           (not enabled?)))
        {:keys [label description]} (feature-label tr tr-quiet feature)]
    (merge {:id        (str (:db/id group) "-" (name feature))
            :label     label
            :toggled?  enabled?
            :disabled? disabled?
            :on-toggle #(>evt [::set-feature {:tenant-id tenant-id
                                              :group-id  (:db/id group)
                                              :feature   feature
                                              :features  (:group/features group)
                                              :enabled?  %
                                              :version   (:entity/version group)}])}
           (when-let [desc (or description (requirement-description tr feature))]
             {:description desc})
           (when issue
             {:title (tr [issue])}))))

;; ---------------------------------------------------------------------------
;; Sections
;; ---------------------------------------------------------------------------

(defn- section-heading
  "Section title plus how many of its features are on, mirroring the design."
  [title enabled-count total toggle-fn _collapsed?]
  [:h2.flex.items-center.gap-3.cursor-pointer.normal-case
   {:on-click toggle-fn}
   [:span title]
   [:span.text-sm.font-normal.text-gray-600
    (str enabled-count " / " total)]])

(rf/reg-sub ::authority-groups
  (fn [_ [_ domain-group]]
    (->> domain-group
         group-model/descendant-authority-groups
         (filter group-model/application-or-upper-tier-authority?)
         (sort-by :group/name))))

(rf/reg-sub ::domain-features
  :<- [::mg/domain-features]
  (fn [features [_ group country]]
    (available-features group country features)))

(rf/reg-sub ::ag-features
  :<- [::mg/authority-features]
  (fn [features [_ group country]]
    (available-features group country features)))

(rf/reg-sub ::group-feature-rows
  :<- [:tr]
  :<- [:tr/quiet]
  :<- [::mg/mobile-app-safe-group-ids]
  :<- [::ff/enabled-flags]
  (fn [[tr tr-quiet mobile-app-safe? global-flags] [_ group country tenant-id]]
    (->> (if (group-model/domain? group)
           (<sub [::mg/domain-features group country])
           (<sub [::mg/authority-features group country]))
         (map (fn [feature]
                (feature-row {:tr               tr
                              :tr-quiet         tr-quiet
                              :tenant-id        tenant-id
                              :group            group
                              :feature          feature
                              :global-flags     global-flags
                              :mobile-app-safe? mobile-app-safe?})))
         (sort-by :label))))

(defn- feature-section
  "A compact, initially collapsed section wrapping a toggle list."
  [{:keys [tr id title group country tenant-id]}]
  (let [rows (<sub [::group-feature-rows group country tenant-id])]
    [section/section
     id
     {:tr                   tr
      :class                "compact !my-0"
      :initially-collapsed? true
      :heading-class        "normal-case"
      :heading-component    (partial section-heading
                                     title
                                     (count (filter :toggled? rows))
                                     (count rows))}
     nil
     [ui-toggle-list/toggle-list {:items rows}]]))

(defn features-tab [tr {:keys [group country]}]
  (let [authority-groups (<sub [::authority-groups group])
        ;; Every section's toggles are routed through the tenant, whichever of
        ;; its groups the feature belongs to.
        tenant-id        (:db/id group)]
    [:div.flex.flex-col.gap-2.mt-6
     [feature-section {:tr        tr
                       :id        :tenant-features-domain
                       :title     (tr [:admin/domain-group-features])
                       :group     group
                       :country   country
                       :tenant-id tenant-id}]
     (for-frag [authority-group authority-groups]
       [feature-section {:tr        tr
                         :id        (str "tenant-features-" (:db/id authority-group))
                         :title     (:group/name authority-group)
                         :group     authority-group
                         :country   country
                         :tenant-id tenant-id}])]))
